import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import AccountPageShell from "../components/account/AccountPageShell";

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");

  const userId = searchParams.get("userId");
  const resetId = searchParams.get("resetId");
  const email = searchParams.get("email");

  useEffect(() => {
    if (!userId || !resetId || !email) {
      window.location.href = "/forgot-password";
    }
    setIsValidating(false);
  }, [userId, resetId, email]);

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  const handleConfirmPasswordChange = (event) => {
    setConfirmPassword(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFieldErrors({});
    setSuccessMessage("");

    if (password !== confirmPassword) {
      setFieldErrors({ confirmPassword: "Passwords do not match" });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/users/password-reset/reset`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId: userId,
            resetId: resetId,
            newPassword: password,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (data.errors && Array.isArray(data.errors)) {
          const errors = {};
          data.errors.forEach((error) => {
            errors[error.path] = error.msg;
          });
          setFieldErrors(errors);
        } else {
          setFieldErrors({ password: data.message });
        }
        setIsLoading(false);
        return;
      }

      setSuccessMessage("Password reset successful! Redirecting to login...");
      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
    } catch (error) {
      console.error("Password reset failed:", error);
      setFieldErrors({ password: error.message || "An error occurred during password reset" });
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <AccountPageShell>
        <div role="status" className="flex min-h-[calc(100vh-4rem)] items-center justify-center gap-3 text-sm text-gray-300">
          <span aria-hidden="true" className="h-5 w-5 animate-spin rounded-full border-2 border-line-strong border-t-accent" />
          Validating reset link…
        </div>
      </AccountPageShell>
    );
  }

  return (
    <AccountPageShell>
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-7xl items-center justify-center px-5 py-10 sm:px-8 lg:px-10">
        <section className="w-full max-w-lg overflow-hidden rounded-xl border border-line bg-surface-850">
          <div className="border-b border-line px-5 py-5 sm:px-7">
            <p className="eyebrow-accent">ACCOUNT SECURITY</p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl">Create a new password</h1>
            <p className="mt-2 text-sm leading-6 text-gray-400">Choose a new password to secure your account.</p>
          </div>

          <div className="p-5 sm:p-7">
            {successMessage && (
              <div role="status" aria-live="polite" className="mb-5 rounded-lg border border-emerald-900/50 bg-emerald-950/20 px-4 py-3 text-sm text-emerald-200">
                {successMessage}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="password" className="field-label">New password</label>
                <input
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Enter your new password"
                  disabled={isLoading}
                  aria-invalid={Boolean(fieldErrors.password)}
                  aria-describedby={fieldErrors.password ? "reset-password-error" : undefined}
                  className="field disabled:cursor-not-allowed disabled:opacity-60"
                />
                {fieldErrors.password && <p id="reset-password-error" role="alert" className="mt-1.5 text-sm text-accent-soft">{fieldErrors.password}</p>}
              </div>
              <div>
                <label htmlFor="confirmPassword" className="field-label">Confirm new password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  placeholder="Re-enter your new password"
                  disabled={isLoading}
                  aria-invalid={Boolean(fieldErrors.confirmPassword)}
                  aria-describedby={fieldErrors.confirmPassword ? "confirm-password-error" : undefined}
                  className="field disabled:cursor-not-allowed disabled:opacity-60"
                />
                {fieldErrors.confirmPassword && <p id="confirm-password-error" role="alert" className="mt-1.5 text-sm text-accent-soft">{fieldErrors.confirmPassword}</p>}
              </div>
              <button type="submit" disabled={isLoading} className="btn-primary min-h-11 w-full gap-2">
                {isLoading && <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                {isLoading ? "Resetting password…" : "Reset password"}
              </button>
            </form>
          </div>
        </section>
      </div>
    </AccountPageShell>
  );
}

export default ResetPassword;
